import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import {
  getTasks,
  createTask,
  updateTask,
  subscribe as subscribeTasks,
} from '../../../services/taskStore';
import { proposeChange } from '../../../services/approvalStore';
import {
  hasPermission,
  isApprovalGated,
  TASK_ROUTING,
  ROLES,
} from '../../../services/rbac';
import { getUser, getUserOptions } from '../../../services/userDirectory';
import { triggerTextDownload, dateStamp } from '../../../services/downloadHelpers';

/**
 * AssessmentDetailPanel
 * ─────────────────────
 * Detail view for a joint assessment with three working tabs:
 *
 *   Overview, read-only assessment metadata + status change (approval-gated)
 *   Tasks   , auto-assigned tasks via taskStore (RBAC-routed by task type)
 *   Documents, real working Download (text export) and View (modal preview)
 *   Timeline, read-only audit trail
 *
 * Save / status changes go through approvalStore.proposeChange when the
 * action is in the APPROVAL_GATED_ACTIONS set (see rbac.js). The user
 * sees a toast: "Submitted to approver, change pending review".
 */
const AssessmentDetailPanel = ({ assessment, onClose, onStatusUpdate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    type: 'general',
    assignee: '',
    dueDate: '',
  });
  const [autoAssignNote, setAutoAssignNote] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [docPreview, setDocPreview] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [editedNotes, setEditedNotes] = useState('');
  const [originalNotes, setOriginalNotes] = useState('');

  // ─── Current user & role ─────────────────────────────────────────────────
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const currentRole = currentUser?.role || 'admin';
  const currentUserId = currentUser?.email || 'admin@clearequal.com';
  const canAssignTask = hasPermission(currentRole, 'assessment.assignTask');
  const canEditFields = hasPermission(currentRole, 'assessment.editFields');
  const canChangeStatus = hasPermission(currentRole, 'assessment.changeStatus');

  // ─── Load tasks for this assessment ──────────────────────────────────────
  useEffect(() => {
    if (!assessment?.id) return;
    setTasks(getTasks(assessment.id));
    return subscribeTasks(() => setTasks(getTasks(assessment.id)));
  }, [assessment?.id]);

  // ─── Initialize editable notes ───────────────────────────────────────────
  useEffect(() => {
    const initial = assessment?.notes || '';
    setOriginalNotes(initial);
    setEditedNotes(initial);
  }, [assessment?.id]);

  if (!assessment) return null;

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in-review', label: 'In Review' },
    { value: 'remediation', label: 'Remediation' },
    { value: 'closed', label: 'Closed' }
  ];

  const taskTypeOptions = [
    { value: 'data-review',           label: 'Data review (auto → Manager)' },
    { value: 'market-analysis',       label: 'Market analysis (auto → Admin)' },
    { value: 'remediation-plan',      label: 'Remediation plan (auto → Admin)' },
    { value: 'works-council-liaison', label: 'Works council liaison (auto → Approver)' },
    { value: 'document-review',       label: 'Document review (auto → Approver)' },
    { value: 'employee-comms',        label: 'Employee comms (auto → Manager)' },
    { value: 'legal-review',          label: 'Legal review (auto → Approver)' },
    { value: 'general',               label: 'General task (auto → Admin)' },
  ];

  // Mock documents (still mock, real file uploads need backend)
  const mockDocuments = [
    {
      id: 1,
      name: 'Initial Gap Analysis Report.pdf',
      uploadedBy: 'Sarah Chen',
      uploadDate: '2025-01-08',
      size: '2.4 MB',
      content: `INITIAL GAP ANALYSIS REPORT
Generated: 2025-01-08 by Sarah Chen
Assessment: ${assessment.caseId || assessment.id}
Department: ${assessment?.department}

EXECUTIVE SUMMARY
─────────────────
Pay gap analysis for ${assessment?.department} department revealed a ${assessment?.gapPercentage}% mean
gender pay gap, exceeding the 5% threshold under EU Pay Transparency Directive 2023/970 Art. 10.

METHODOLOGY
───────────
• Comparison cohort: 24 affected employees across 3 job levels
• Statistical control: experience, role, location
• Reference period: rolling 12 months ending Q4 2024

KEY FINDINGS
────────────
1. Gap concentrated in mid-level positions (L3-L4)
2. No statistically significant gap at L5+ leadership tiers
3. Variable pay component shows 7.2% gap (higher than base)
4. Geographic distribution: gap most pronounced in DACH region

RECOMMENDATIONS
───────────────
• Immediate market salary benchmarking for affected roles
• Review of historical promotion criteria for bias indicators
• Engagement with works council for joint remediation plan
`,
    },
    {
      id: 2,
      name: 'Market Salary Research.xlsx',
      uploadedBy: 'Michael Rodriguez',
      uploadDate: '2025-01-12',
      size: '1.8 MB',
      content: `Market Salary Benchmark, ${assessment?.department}
Source: Mercer / Willis Towers Watson, 2024 Q4
Region: ${assessment?.country || 'Germany'}

Job Level   | Median   | 25th    | 75th    | Our P50  | Variance
L3 Engineer | €68,400  | €61,200 | €76,800 | €64,100  | -6.3%
L4 Senior   | €82,500  | €74,800 | €91,300 | €78,200  | -5.2%
L5 Principal| €106,200 | €96,400 | €118,500| €107,100 | +0.8%

Notes: Sub-market positioning at L3 and L4 correlates with the observed
in-band pay gap. Bringing L3-L4 medians to market parity would resolve
~62% of the measured gap before any gender-specific remediation.
`,
    },
    {
      id: 3,
      name: 'Remediation Action Plan.docx',
      uploadedBy: 'Emma Thompson',
      uploadDate: '2025-01-14',
      size: '856 KB',
      content: `REMEDIATION ACTION PLAN (Draft v0.3)
Author: Emma Thompson · Status: Pending works council review

Phase 1, Immediate (Q1 2025)
• Adjust L3 base salaries to market median (~€2,200/yr avg uplift, 14 employees)
• Adjust L4 base salaries to market median (~€3,100/yr avg uplift, 8 employees)
• Document changes in audit trail per Art. 10(3) requirements

Phase 2, Structural (Q2 2025)
• Revise variable pay calculation methodology
• Codify promotion criteria with measurable, role-based gates
• Quarterly pay equity review cadence

Phase 3, Verification (Q3 2025)
• Re-run gap analysis with adjusted compensation
• Independent statistical validation
• Joint sign-off with works council representatives

Estimated total cost: €68,400 (annualized recurring)
EU Pay Transparency Directive Art. 10 compliance: full
`,
    },
  ];

  const mockTimeline = [
    { id: 1, event: 'Assessment Triggered',  description: 'Pay gap exceeded 5% threshold for 6 months', date: '2025-01-05', user: 'System',     type: 'trigger' },
    { id: 2, event: 'Case Assigned',         description: 'Auto-assigned to Sarah Chen (HR Admin) for initial review', date: '2025-01-06', user: 'System (RBAC)', type: 'assignment' },
    { id: 3, event: 'Status Updated',        description: 'Changed from Open to In Review',             date: '2025-01-08', user: 'Sarah Chen', type: 'status' },
    { id: 4, event: 'Document Uploaded',     description: 'Initial Gap Analysis Report added',          date: '2025-01-08', user: 'Sarah Chen', type: 'document' }
  ];

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleDateString('en-GB'); }
    catch { return String(d); }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      'open':         { color: 'bg-warning text-warning-foreground',   label: 'Open' },
      'in-review':    { color: 'bg-primary text-primary-foreground',   label: 'In Review' },
      'remediation':  { color: 'bg-accent text-accent-foreground',     label: 'Remediation' },
      'closed':       { color: 'bg-muted text-muted-foreground',       label: 'Closed' },
      'completed':    { color: 'bg-success text-success-foreground',   label: 'Completed' },
      'in-progress':  { color: 'bg-primary text-primary-foreground',   label: 'In Progress' },
      'pending':      { color: 'bg-warning text-warning-foreground',   label: 'Pending' }
    };
    const c = cfg[status] || cfg['open'];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  const getTimelineIcon = (type) => ({
    trigger: 'AlertTriangle', assignment: 'UserPlus', status: 'RefreshCw', document: 'FileText',
  }[type] || 'Circle');

  // ─── Auto-assignment preview when task type changes ──────────────────────
  useEffect(() => {
    if (!showTaskForm || newTask.assignee) {
      setAutoAssignNote(null);
      return;
    }
    const route = TASK_ROUTING[newTask.type];
    if (!route) return;
    const opts = getUserOptions(route.role);
    if (opts.length > 0) {
      const dept = assessment?.department || null;
      const deptMatch = route.scope === 'department' && dept
        ? opts.find((o) => o.label.includes(dept))
        : null;
      const target = deptMatch || opts[0];
      setAutoAssignNote({
        userId: target.value,
        userLabel: target.label,
        role: route.role,
        scope: route.scope,
        usedDeptMatch: !!deptMatch,
      });
    }
  }, [newTask.type, newTask.assignee, showTaskForm, assessment?.department]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleAddTask = () => {
    if (!canAssignTask) {
      setSaveMessage({ type: 'error', text: 'Your role cannot create tasks.' });
      return;
    }
    if (!newTask.title || !newTask.dueDate) {
      setSaveMessage({ type: 'error', text: 'Title and due date are required.' });
      return;
    }
    const task = createTask({
      assessmentId: assessment.id,
      title: newTask.title,
      type: newTask.type,
      assignee: newTask.assignee || null, // null triggers auto-assignment
      department: assessment?.department,
      dueDate: newTask.dueDate,
      createdBy: currentUserId,
    });
    setNewTask({ title: '', type: 'general', assignee: '', dueDate: '' });
    setShowTaskForm(false);
    setAutoAssignNote(null);
    const assignedTo = getUser(task.assignee);
    setSaveMessage({
      type: 'success',
      text: task.autoAssigned
        ? `Task created, auto-assigned to ${assignedTo?.name || task.assignee} based on RBAC routing.`
        : `Task created, assigned to ${assignedTo?.name || task.assignee}.`,
    });
    setTimeout(() => setSaveMessage(null), 5000);
  };

  const handleToggleTask = (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    updateTask(task.id, { status: newStatus }, currentUserId);
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === assessment?.status) return;
    if (!canChangeStatus) {
      setSaveMessage({ type: 'error', text: 'Your role cannot change assessment status.' });
      return;
    }
    setPendingStatus(newStatus);
  };

  const handleConfirmStatusChange = () => {
    const action = pendingStatus === 'closed' ? 'assessment.close' : 'assessment.changeStatus';
    if (isApprovalGated(action)) {
      proposeChange({
        action,
        targetType: 'assessment',
        targetId: assessment.id,
        patch: { status: pendingStatus },
        proposedBy: currentUserId,
        reason: `Status change from "${assessment.status}" to "${pendingStatus}"`,
      });
      setSaveMessage({
        type: 'pending',
        text: `Status change submitted for approver review. The assessment status will update once an approver ratifies it.`,
      });
    } else {
      onStatusUpdate?.(assessment.id, pendingStatus);
      setSaveMessage({ type: 'success', text: 'Status updated.' });
    }
    setPendingStatus(null);
    setTimeout(() => setSaveMessage(null), 6000);
  };

  const handleSaveChanges = () => {
    const notesChanged = editedNotes !== originalNotes;

    if (!notesChanged && !pendingStatus) {
      setSaveMessage({ type: 'info', text: 'No changes to save.' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    if (!canEditFields) {
      setSaveMessage({ type: 'error', text: 'Your role cannot edit this assessment.' });
      return;
    }

    // Notes are not approval-gated, apply immediately and persist locally
    if (notesChanged) {
      try {
        const key = `clearequal.assessment.${assessment.id}.notes`;
        localStorage.setItem(key, editedNotes);
        setOriginalNotes(editedNotes);
      } catch (_) {}
    }

    if (pendingStatus) {
      handleConfirmStatusChange();
    } else {
      setSaveMessage({ type: 'success', text: 'Changes saved.' });
      setTimeout(() => setSaveMessage(null), 4000);
    }
  };

  // ─── Document download / view (real handlers) ────────────────────────────
  const handleDownloadDoc = (doc) => {
    const filename = doc.name.replace(/\.(pdf|docx|xlsx)$/i, '.txt');
    const header = `${'='.repeat(70)}\n${doc.name}\n${'='.repeat(70)}\nUploaded by: ${doc.uploadedBy}\nUpload date: ${doc.uploadDate}\nSize: ${doc.size}\nExported: ${dateStamp()} via ClearEqual\n${'='.repeat(70)}\n\n`;
    triggerTextDownload(filename, header + (doc.content || 'No content available.'));
  };

  const handleViewDoc = (doc) => setDocPreview(doc);

  const tabs = [
    { id: 'overview',  label: 'Overview',  icon: 'Info' },
    { id: 'tasks',     label: 'Tasks',     icon: 'CheckSquare' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'timeline',  label: 'Timeline',  icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-foreground">
              Assessment Details, {assessment?.caseId || assessment?.id}
            </h2>
            {getStatusBadge(assessment?.status)}
          </div>
          <Button variant="ghost" iconName="X" onClick={onClose} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border bg-muted/30">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary bg-card'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Save message */}
        {saveMessage && (
          <div className={`px-6 py-3 text-sm border-b ${
            saveMessage.type === 'success' ? 'bg-success/10 text-success border-success/30' :
            saveMessage.type === 'error'   ? 'bg-destructive/10 text-destructive border-destructive/30' :
            saveMessage.type === 'pending' ? 'bg-warning/10 text-warning border-warning/30' :
            'bg-muted text-muted-foreground border-border'
          }`}>
            <Icon name={
              saveMessage.type === 'success' ? 'CheckCircle' :
              saveMessage.type === 'error' ? 'AlertCircle' :
              saveMessage.type === 'pending' ? 'Clock' : 'Info'
            } size={14} className="inline mr-2" />
            {saveMessage.text}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ─── OVERVIEW ─────────────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">Assessment Info</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Department</label>
                      <p className="text-foreground">{assessment?.department || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Trigger Date</label>
                      <p className="text-foreground">{formatDate(assessment?.triggerDate)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Due Date</label>
                      <p className="text-foreground">{formatDate(assessment?.dueDate)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">Assignment</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Currently Assigned To</label>
                      <p className="text-foreground">{assessment?.assignee || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Priority</label>
                      <p className="text-foreground capitalize">{assessment?.priority || 'N/A'}</p>
                    </div>
                    <div>
                      <Select
                        label="Update Status (proposes change for approval)"
                        options={statusOptions}
                        value={pendingStatus || assessment?.status}
                        onChange={handleStatusChange}
                        disabled={!canChangeStatus}
                      />
                      {pendingStatus && (
                        <p className="text-xs text-warning mt-1">
                          <Icon name="Clock" size={12} className="inline mr-1" />
                          Pending, click Save Changes to submit to approver
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Gap Analysis */}
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">Gap Analysis</h3>
                <div className="bg-muted rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-foreground">24</p>
                      <p className="text-sm text-muted-foreground">Affected Employees</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-destructive">{assessment?.gapPercentage}%</p>
                      <p className="text-sm text-muted-foreground">Pay Gap</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">€45,600</p>
                      <p className="text-sm text-muted-foreground">Total Gap Amount</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Editable notes */}
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">Internal Notes</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Free-text working notes. Saved with audit timestamp; not part of the formal Article 9 disclosure.
                </p>
                <textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  disabled={!canEditFields}
                  rows={5}
                  placeholder="Add notes about this assessment..."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                />
                {editedNotes !== originalNotes && (
                  <p className="text-xs text-warning mt-1">
                    <Icon name="Edit3" size={12} className="inline mr-1" />
                    Unsaved changes, click Save Changes
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ─── TASKS ────────────────────────────────────────────────── */}
          {activeTab === 'tasks' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-foreground">Tasks</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tasks auto-assign by type via RBAC routing. Override the suggested assignee if needed.
                  </p>
                </div>
                {canAssignTask && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    onClick={() => setShowTaskForm(!showTaskForm)}
                  >
                    Add Task
                  </Button>
                )}
              </div>

              {showTaskForm && (
                <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-foreground">New Task</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Task Title"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      placeholder="e.g. Review Q1 salary adjustments"
                    />
                    <Select
                      label="Task Type"
                      options={taskTypeOptions}
                      value={newTask.type}
                      onChange={(value) => setNewTask({ ...newTask, type: value, assignee: '' })}
                    />
                  </div>

                  {/* Auto-assign suggestion banner */}
                  {autoAssignNote && (
                    <div className="bg-primary/5 border border-primary/30 rounded p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Sparkles" size={14} className="text-primary" />
                        <span className="font-medium text-foreground">Auto-assignment suggestion</span>
                      </div>
                      <p className="text-muted-foreground">
                        Based on task type <code className="bg-background px-1 rounded">{newTask.type}</code> and{' '}
                        {autoAssignNote.scope === 'department' ? `department "${assessment?.department}"` : 'global scope'},
                        this will be assigned to <strong className="text-foreground">{autoAssignNote.userLabel}</strong>{' '}
                        ({autoAssignNote.role} role){autoAssignNote.usedDeptMatch ? ', department match' : ''}.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Override below, or leave Assignee blank to use this suggestion.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Assignee (optional override)"
                      options={[{ value: '', label: ', Use auto-assignment,' }, ...getUserOptions()]}
                      value={newTask.assignee}
                      onChange={(value) => setNewTask({ ...newTask, assignee: value })}
                    />
                    <Input
                      label="Due Date"
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    />
                  </div>

                  <div className="flex space-x-2">
                    <Button variant="default" size="sm" onClick={handleAddTask}>
                      Create Task
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowTaskForm(false); setAutoAssignNote(null); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Task list */}
              <div className="space-y-3">
                {tasks.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No tasks yet. Click "Add Task" to create one.
                  </p>
                )}
                {tasks.map((task) => {
                  const assignee = getUser(task.assignee);
                  return (
                    <div key={task.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1">
                          <Checkbox
                            checked={task.status === 'completed'}
                            onChange={() => handleToggleTask(task)}
                          />
                          <div className="flex-1">
                            <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {task.title}
                            </h4>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              <Icon name="User" size={12} className="inline mr-1" />
                              {assignee?.name || task.assignee || 'Unassigned'}
                              {task.autoAssigned && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">
                                  auto-assigned
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(task.status)}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground ml-7">
                        <span>Due: {formatDate(task.dueDate)}</span>
                        {task.completedAt && (
                          <span>Completed: {formatDate(task.completedAt)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── DOCUMENTS ─────────────────────────────────────────────── */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Documents</h3>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Upload"
                  onClick={() => setSaveMessage({
                    type: 'info',
                    text: 'Document upload requires backend storage (not in UAT scope). See deployment guide.'
                  })}
                >
                  Upload Document
                </Button>
              </div>

              <div className="space-y-3">
                {mockDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div className="flex items-center space-x-3 flex-1">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">{doc.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          Uploaded by {doc.uploadedBy} on {formatDate(doc.uploadDate)} · {doc.size}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm" iconName="Eye" onClick={() => handleViewDoc(doc)}>
                        View
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Download" onClick={() => handleDownloadDoc(doc)}>
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── TIMELINE ─────────────────────────────────────────────── */}
          {activeTab === 'timeline' && (
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Timeline</h3>
              <div className="space-y-0">
                {mockTimeline.map((event, index) => (
                  <div key={event.id} className="flex items-start space-x-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                        <Icon name={getTimelineIcon(event.type)} size={16} color="white" />
                      </div>
                      {index < mockTimeline.length - 1 && (
                        <div className="w-px h-8 bg-border mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-foreground">{event.event}</h4>
                        <span className="text-sm text-muted-foreground">{formatDate(event.date)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{event.description}</p>
                      <p className="text-xs text-muted-foreground">by {event.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <span className="text-xs text-muted-foreground">
            Logged in as <strong className="text-foreground">{currentUser?.name || currentUserId}</strong> ({currentRole})
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="default"
              iconName="Save"
              onClick={handleSaveChanges}
              disabled={!canEditFields && !canChangeStatus}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Document preview modal */}
      {docPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setDocPreview(null)}>
          <div
            className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="font-semibold text-foreground">{docPreview.name}</h3>
                <p className="text-xs text-muted-foreground">
                  Uploaded by {docPreview.uploadedBy} · {docPreview.size}
                </p>
              </div>
              <Button variant="ghost" iconName="X" onClick={() => setDocPreview(null)} />
            </div>
            <pre className="flex-1 overflow-auto p-6 text-xs font-mono text-foreground whitespace-pre-wrap leading-relaxed">
              {docPreview.content}
            </pre>
            <div className="flex items-center justify-end gap-2 p-3 border-t border-border bg-muted/20">
              <Button variant="outline" size="sm" iconName="Download" onClick={() => handleDownloadDoc(docPreview)}>
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDocPreview(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentDetailPanel;
